
import { EventEmitter, Output, Input, Directive } from '@angular/core';
import {TylerMatTableDataSource} from '../../angular-material/extended.class';
import {FilterModel} from './filter.model';


/**
 * This is the contents of a filter. This can be useful when trying to build out a pusher filter which has the same contents as
 * the table filters.
 *
 * @Input dateValue: this is the date value for date data types
 * @Output dateValueChange: this allows you to use two-way binding on date values
 *
 * @Input filterValue: this is a string value for string or enumeration data types
 * @Ouput filterValueChange: this allows you to use two-way binding on filter values
 *
 * @Input operation: this is the operation of the filter
 * @Output operationChange: this allows you to use two-way binding on operation
 *
 * @Output menuTrigger: this is triggered when an encapsulating menu should close
 *
 * REQUIRED @Input data: this is the data source being panelFiltered on
 * REQUIRED @Input clearFilterEvent: this is an event emitter that fires whenever the filter is cleared from outside the component.
 * REQUIRED @Input dataType: this is the type of the data. Accepted values are number, boolean, string, date, and enumeration
 * REQUIRED @Input columnName: this is the name of the column your filtering on
 *
 * @Input columnIndex: used when filtering on string[] rather than objects
 * @Input enumerationValues: this is a list of strings that can be selected for enumeration data types
 *
 * @Input valueWidth: the width of the filter input. Defaults to 180px.
 * @Input operationMarginRight: the space to the right of the operation box. Defaults to 0px.
 * @Input floatLabel: whether or not labels should float above the input. Defaults to auto.
 * @Input fieldsMarginBottom: the size of the bottom margin. Defaults to 0px.
 * @Input showLabel: determines whether or not the label shows up. Defaults to true.
 *
 * @Output clickEvent: this event fires when the filter button is clicked
 * @Output filterChange: this event fires when the data have been changed by this component. The event is the data.
 * @Output filterOperationChange: this event fires when the operation has been changed by this component. The event is the operation.
 */
@Directive()
// tslint:disable-next-line:directive-class-suffix
export class HasFilter {

	// Properties
	@Output() dateValueChange = new EventEmitter<Date>();
	@Output() filterValueChange = new EventEmitter<string>();
	@Output() operationChange = new EventEmitter<string>();
	@Output() showTrueChange = new EventEmitter<boolean>();
	@Output() showFalseChange = new EventEmitter<boolean>();

	@Input() data: TylerMatTableDataSource;
	@Input() columnName: string;
	@Input() columnIndex: number;
	@Input() dataType: 'string' | 'number' | 'date' | 'boolean' | 'enumeration' | 'nullable boolean';
	@Output() clickEvent = new EventEmitter<string>();
	@Input() enumerationValues: string[] = [];
	@Input() trueValue = 'Y';
	@Input() indeterminateValue = '';
	@Input() falseValue = '';
	@Input() showTrueLabel = 'Y';
    @Input() showIndeterminateLabel = '';
	@Input() showFalseLabel = '';
	@Input() debounceTime = 0;

	@Output() filterChange = new EventEmitter<any>();
	@Output() filterOperationChange = new EventEmitter<any>();

	// Class Functions
	get dateValue(): Date {
	    if (this.data) {
            return this.filter.dateValue;
        } else {
	        return null;
        }
	}

	@Input() set dateValue(value: Date) {
	    if (this.data) {
            this.filter.dateValue = value;
            this.dateValueChange.emit(value);
            this.data.filter = 'trigger';
        }
	}

	get filterValue(): string {
	    if (this.data) {
            return this.filter.value;
        } else {
	        return '';
        }
	}

	@Input() set filterValue(value: string) {
	    if (this.data) {
            this.filter.value = value;
            this.filterValueChange.emit(value);
            this.data.filter = 'trigger';
        }
	}

	get operation(): string {
	    if (this.data) {
            return this.filter.operator;
        } else {
	        return '';
        }
	}

	@Input() set operation(value: string) {
	    if (this.data) {
            if (!value) {
                this.filter.operator = '0';
            } else {
                this.filter.operator = value;
            }
            if (this.dataType == 'boolean') {
                if (this.filter.operator == '1') {
                    this.filter.value = this.trueValue;
                } else {
                    this.filter.value = this.falseValue;
                }
            }
            if (this.dataType == 'nullable boolean') {
                if (this.filter.operator == '1') {
                    this.filter.value = this.trueValue;
                } else if (this.filter.operator == '3') {
                    this.filter.value = this.falseValue;
                } else {
                    this.filter.value = this.indeterminateValue;
                }
            }
            this.operationChange.emit(value);
            this.data.filter = 'trigger';
        }
	}

	get filtered(): boolean {
		if (this.dataType.includes('boolean')) {
			return this.operation != '0';
		} else {
			return (this.filterValue != '' && this.filterValue != undefined && this.filterValue != null) ||
				(this.dateValue != undefined && this.dateValue != null && this.dateValue.toString() != '' && this.dateValue.toString() != 'Invalid Date');
		}
	}

	get filter(): FilterModel {
	    if (this.data) {
            const filter = this.data.getFilter(this.columnName);
            if (filter) {
                return filter;
            } else {
                const newFilter = new FilterModel('', this.columnName, '0', this.dataType, null);
                if (this.columnIndex) {
                    newFilter.columnIndex = this.columnIndex;
                }
                this.data.addFilter(newFilter);
                return newFilter;
            }
        } else {
	        return null;
        }
	}

	// Methods
	applyFilter() {
		// NOTE: Depreciated. This is no longer necessary.
		// POST: applies the filter
		//
		// // Date types
		// if (this.dataType == 'date' && this.dateValue) {
		// 	const newFilter = new FilterModel(this.dateValue.toLocaleDateString(), this.columnName, this.operation, this.dataType, this.dateValue);
		// 	this.data.addRemoveFilter(newFilter, this.data);
		// }
		//
		// // Boolean types
		// else if (this.dataType == 'boolean') {
		// 	const trueFilter = new FilterModel(this.trueValue, this.columnName, this.operation, this.dataType, this.dateValue);
		// 	const falseFilter = new FilterModel(this.falseValue, this.columnName, this.operation, this.dataType, this.dateValue);
		// 	if (this.operation == '0') {
		// 		this.data.removeFilter(trueFilter, this.data);
		// 		this.data.removeFilter(falseFilter, this.data);
		// 	} else if (this.operation == '1') {
		// 		this.data.removeFilter(falseFilter, this.data);
		// 		this.data.addFilter(trueFilter, this.data);
		// 	} else if (this.operation == '2') {
		// 		this.data.removeFilter(trueFilter, this.data);
		// 		this.data.addFilter(falseFilter, this.data);
		// 	}
		// }
		//
		// // Other types
		// else {
		// 	const newFilter = new FilterModel(this.filterValue, this.columnName, this.operation, this.dataType, this.dateValue);
		// 	if (this.columnIndex) {
		// 		newFilter.columnIndex = this.columnIndex;
		// 	}
		// 	this.data.addRemoveFilter(newFilter, this.data);
		// }
	}

	removeClick() {
		switch (this.dataType) {
			case 'date':
				this.dateValue = undefined;
				this.filterChange.emit(undefined);
				break;
			case 'string':
				this.filterValue = '';
				this.filterChange.emit('');
				break;
			default:
				this.dateValue = undefined;
				this.filterValue = '';
				break;
		}
	}
}
